<template>
  <v-col cols="12" md="6" lg="4">
    <div class="package_card">
      <div class="package_card__header">
        <v-chip small :color="statusColor" text-color="white">
          {{ statusLabel }}
        </v-chip>
        <span class="package_card__index">#{{ index + 1 }}</span>
      </div>

      <div class="package_card__number">
        {{ pkg.package_number }}
      </div>

      <div class="package_card__body">
        <div class="package_info_row">
          <i class="fal fa-user"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.client_name") }}:</span>
          <span class="package_info_row__value">{{ pkg.user?.name || "-" }}</span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-phone"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.contact_number") }}:</span>
          <span class="package_info_row__value" dir="ltr">{{ pkg.user?.mobile || "-" }}</span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-barcode"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.package_number") }}:</span>
          <span class="package_info_row__value">{{ pkg.package_number || "-" }}</span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-weight"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.package_weight") }}:</span>
          <span class="package_info_row__value">
            {{ pkg.weight || "-" }} {{ $t("PLACEHOLDERS.kg") }}
          </span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-cube"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.package_dimensions") }}:</span>
          <span class="package_info_row__value">{{ pkg.dimensions || "-" }}</span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-truck"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.delivery_type") }}:</span>
          <span class="package_info_row__value">{{ serviceTypeLabel }}</span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-coins"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.bundle_package_price") }}:</span>
          <span class="package_info_row__value">
            {{ pkg.delivery_fee || 0 }} {{ $t("PLACEHOLDERS.sar_currency") }}
          </span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-city"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.bundle_city") }}:</span>
          <span class="package_info_row__value">{{ pkg.city || "-" }}</span>
        </div>

        <div class="package_info_row">
          <i class="fal fa-map-marker-alt"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.address") }}:</span>
          <span class="package_info_row__value package_info_row__value--truncate" :title="pkg.address">
            {{ pkg.address || "-" }}
          </span>
        </div>

        <div class="package_info_row" v-if="pkg.notes">
          <i class="fal fa-sticky-note"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.notes") }}:</span>
          <span class="package_info_row__value">{{ pkg.notes }}</span>
        </div>

        <div class="package_info_row" v-if="pkg.expected_delivery_time">
          <i class="fal fa-clock"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.expected_delivery_time") }}:</span>
          <span class="package_info_row__value">{{ pkg.expected_delivery_time }}</span>
        </div>

        <div
          class="package_info_row"
          v-if="pkg.status === 'delivered' && pkg.delivery_duration"
        >
          <i class="fal fa-check-circle text-success"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.delivery_time_taken") }}:</span>
          <span class="package_info_row__value text-success">{{ pkg.delivery_duration }}</span>
        </div>

        <div
          class="package_info_row"
          v-if="pkg.status === 'delivered' && pkg.distribution_point_duration"
        >
          <i class="fal fa-hourglass-half text-warning"></i>
          <span class="package_info_row__label">{{ $t("PLACEHOLDERS.distribution_point_time") }}:</span>
          <span class="package_info_row__value text-warning">{{
            pkg.distribution_point_duration
          }}</span>
        </div>

        <div v-if="pkg.return_source" class="package_card__return">
          <div class="package_info_row">
            <i class="fal fa-undo text-danger"></i>
            <span class="package_info_row__label">{{ $t("PLACEHOLDERS.return_source") }}:</span>
            <span class="package_info_row__value">{{ pkg.return_source }}</span>
          </div>
          <div class="package_info_row" v-if="pkg.return_fee">
            <span class="package_info_row__label ms-4">{{ $t("PLACEHOLDERS.return_fee") }}:</span>
            <span class="package_info_row__value">
              {{ pkg.return_fee }} {{ $t("PLACEHOLDERS.sar_currency") }}
            </span>
          </div>
        </div>
      </div>

      <div class="package_card__qr text-center" v-if="pkg.qr">
        <img
          :src="pkg.qr"
          :alt="pkg.package_number"
          class="qr-thumb"
          @click="$emit('show-qr', pkg.qr, pkg.package_number)"
        />
      </div>

      <div v-if="pkg.history && pkg.history.length" class="package_card__history">
        <package-history-panel :history="pkg.history" />
      </div>

      <div class="package_card__actions">
        <v-btn small outlined color="primary" @click="$emit('show-details', pkg)">
          <i class="fal fa-eye mr-1"></i>
          {{ $t("BUTTONS.show") }}
        </v-btn>
        <v-btn
          v-if="finished"
          small
          outlined
          color="success"
          @click="$emit('view-invoice', pkg)"
        >
          <i class="fal fa-file-alt mr-1"></i>
          {{ $t("BUTTONS.invoice") }}
        </v-btn>
      </div>
    </div>
  </v-col>
</template>

<script>
import {
  getPackageStatusColor,
  getServiceTypeLabel,
  isPackageFinished,
} from "../utils/bundleHelpers";
import PackageHistoryPanel from "./PackageHistoryPanel.vue";

export default {
  name: "BundlePackageCard",

  components: {
    PackageHistoryPanel,
  },

  props: {
    pkg: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },

  computed: {
    statusColor() {
      return getPackageStatusColor(this.pkg.status);
    },
    statusLabel() {
      return this.pkg.status_obj?.text || this.pkg.status;
    },
    serviceTypeLabel() {
      return getServiceTypeLabel(this.pkg.service_type, this.$t.bind(this));
    },
    finished() {
      return isPackageFinished(this.pkg);
    },
  },
};
</script>
